/**
 * Deterministic snapshot hashing for undo/redo and dirty detection.
 *
 * Extracted from EditorApp.tsx for reuse and testability.
 * These are local, bundler-safe implementations to avoid TDZ/cycle issues
 * in minified builds.
 */

/**
 * Deterministic JSON serialization that handles circular references,
 * sorts object keys, and normalizes special types.
 */
export function stableStringify(value: any, seen: WeakSet<object> = new WeakSet()): string {
  if (value === null || typeof value === 'undefined') return 'null';
  const t = typeof value;
  if (t === 'number' || t === 'boolean') return JSON.stringify(value);
  if (t === 'string') return JSON.stringify(value);
  if (t === 'bigint') return JSON.stringify(value.toString());
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (value instanceof RegExp) return JSON.stringify(value.toString());
  if (Array.isArray(value)) {
    return `[${value.map((v) => stableStringify(v, seen)).join(',')}]`;
  }
  if (t === 'object') {
    if (seen.has(value as object)) return '"[Circular]"';
    seen.add(value as object);
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v, seen)}`)
      .join(',')}}`;
  }
  return JSON.stringify(String(value));
}

function pickLayerFields(layer: any) {
  const {
    id, type, x, y, w, h, text, font, fontWeight, fontStyle, align, fill, fillEnabled, stroke,
    src, constrain, mode, maxWidth, boxHeight, locked, visible,
  } = layer || {};
  return {
    id,
    type,
    x,
    y,
    w,
    h,
    text,
    font,
    fontWeight,
    fontStyle,
    align,
    fill,
    fillEnabled,
    stroke,
    src,
    constrain,
    mode,
    maxWidth,
    boxHeight,
    locked: locked === true,
    visible: visible !== false,
  };
}

function normalizeLayerList(layers: any): any[] {
  return Array.isArray(layers) ? layers.map((layer) => pickLayerFields(layer)) : [];
}

function normalizeSnapRows(rows: any): any[] {
  return Array.isArray(rows)
    ? rows.map((row) => ({
        index: Number.isFinite(Number(row?.index)) ? Number(row.index) : 0,
        data: row?.data && typeof row.data === 'object' ? { ...row.data } : {},
        images: row?.images && typeof row.images === 'object' ? { ...row.images } : {},
      }))
    : [];
}

function normalizeRemix(remix: any) {
  return {
    id: remix?.id ?? null,
    name: remix?.name ?? null,
    canvasWidth: Number.isFinite(Number(remix?.canvas_width ?? remix?.canvasWidth))
      ? Number(remix.canvas_width ?? remix.canvasWidth)
      : undefined,
    canvasHeight: Number.isFinite(Number(remix?.canvas_height ?? remix?.canvasHeight))
      ? Number(remix.canvas_height ?? remix.canvasHeight)
      : undefined,
    layers: normalizeLayerList(remix?.layers),
    variables: remix?.variables && typeof remix.variables === 'object' ? { ...remix.variables } : {},
    snapRows: normalizeSnapRows(remix?.snap_rows ?? remix?.snapRows),
    background: remix?.background && typeof remix.background === 'object' ? remix.background : null,
  };
}

/**
 * Extract the subset of a canvas snapshot that affects content identity.
 */
export function normalizeSnapshotForHash(snapshot: any) {
  const s = snapshot || {};
  const layers = normalizeLayerList(s.layers);
  const defaultVars = s.defaultVars && typeof s.defaultVars === 'object' ? { ...s.defaultVars } : {};
  const canvasWidth = Number.isFinite(s.canvasWidth) ? s.canvasWidth : undefined;
  const canvasHeight = Number.isFinite(s.canvasHeight) ? s.canvasHeight : undefined;
  const snapRows = normalizeSnapRows(s.snapRows ?? s.snap_rows);
  const remixes = Array.isArray(s.remixes)
    ? s.remixes.map((remix: any) => normalizeRemix(remix))
    : Array.isArray(s.variations)
      ? s.variations.map((remix: any) => normalizeRemix(remix))
      : [];
  const background = s.background && typeof s.background === 'object' ? s.background : null;
  return { layers, defaultVars, snapRows, remixes, background, canvasWidth, canvasHeight };
}

/**
 * Compute a deterministic string hash of a canvas snapshot.
 */
export function computeSnapshotHash(snapshot: any): string {
  try {
    const norm = normalizeSnapshotForHash(snapshot);
    return stableStringify(norm);
  } catch {
    return stableStringify(snapshot);
  }
}

/**
 * Compute a hash of persisted template data (DB format with snake_case keys).
 * Returns null if data is invalid.
 */
export function computeTemplateDataHash(data: Record<string, unknown> | null | undefined): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  try {
    const layers = Array.isArray((data as any).layers) ? (data as any).layers : [];
    const defaultVarsSource =
      (data as any).default_vars && typeof (data as any).default_vars === 'object'
        ? (data as any).default_vars
        : (data as any).variables && typeof (data as any).variables === 'object'
          ? (data as any).variables
          : {};
    const canvasWidthRaw = (data as any).canvas_width ?? (data as any).canvasWidth;
    const canvasHeightRaw = (data as any).canvas_height ?? (data as any).canvasHeight;
    const remixes = Array.isArray((data as any).remixes)
      ? (data as any).remixes
      : Array.isArray((data as any).variations)
        ? (data as any).variations
        : [];
    const snapshot = {
      layers,
      defaultVars: defaultVarsSource,
      snapRows: Array.isArray((data as any).snap_rows) ? (data as any).snap_rows : [],
      remixes,
      background: (data as any).background,
      canvasWidth: Number.isFinite(Number(canvasWidthRaw)) ? Number(canvasWidthRaw) : undefined,
      canvasHeight: Number.isFinite(Number(canvasHeightRaw)) ? Number(canvasHeightRaw) : undefined,
    };
    return computeSnapshotHash(snapshot);
  } catch (error) {
    console.warn('Failed to compute template data hash', error);
    return null;
  }
}
